import React, { useState } from "react";

import classnames from "classnames";
import { getFamilyInfo } from "EmployeeApplication/api";
import { enrollmentForOverview, chooseCoverageRows } from "EmployeeCoverage/Utils/chooseCoverage";
import { capitalize } from "lodash";
import { useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { notCoveredOrFormatCurrency } from "Utils/formatCurrency";

import { CarrierLogo } from "Cobalt/CarrierLogo";
import Header from "Cobalt/Header";
import { Surface } from "Cobalt/Surface";
import { HeaderTR, TD, TH, TR, Table } from "Cobalt/TableV2";
import Button from "Common/Button";
import { RoutedBackButton } from "Common/forms/buttons";
import { BodyText } from "Theme/BodyText";
import { CardSection } from "Theme/Card";

import ChooseCoverageModal from "./Components/ChooseCoverageModal";

import makeBem from "Utils/makeBem";

const bem = makeBem("EmployeeMECPlan");

const EnrollOrDeclineButton = ({ openModal }) => (
  <Button
    size="medium"
    color="blue"
    className="select-plan center-xs-block"
    text="Enroll or Decline"
    data-keyboard="true"
    onClick={openModal}
    style={{ float: "right" }}
  />
);

const MECHeader = ({ plan, openModal }) => (
  <CardSection className={classnames(bem("Header"), "clearfix")} pt={4}>
    <CarrierLogo pt={2} plan={plan} />
    <BodyText pl={4} pt={3} size="large" weight="bold">
      {plan.display_name}
    </BodyText>
    <EnrollOrDeclineButton openModal={openModal} />
  </CardSection>
);

const CostRow = ({ name, value }) => (
  <TR>
    <TD>{name}</TD>
    <TD>{notCoveredOrFormatCurrency(value)}</TD>
  </TR>
);

const PremiumOverview = ({ prices, familyInfo, contributions }) => (
  <Surface px={3} py={4}>
    <BodyText pb={4} pl={2} size="large" weight="bold">
      Premium Overview
    </BodyText>
    <Table>
      <thead>
        <HeaderTR>
          <TH>Family Member</TH>
          <TH>Your Cost</TH>
        </HeaderTR>
      </thead>
      <tbody>
        {chooseCoverageRows(familyInfo, [prices], contributions, enrollmentForOverview([prices], familyInfo), "medical").map((row) => (
          <CostRow name={row.label} value={row.prices[0]} />
        ))}
      </tbody>
    </Table>
  </Surface>
);

const PlanOverview = ({ plan }) => {
  const drugTier1 = notCoveredOrFormatCurrency(plan.estimated_costs["drug_tier:Tier 1"]);

  const drugTier4 = notCoveredOrFormatCurrency(plan.estimated_costs["drug_tier:Tier 4"]);

  const planDetails = {
    Deductible: notCoveredOrFormatCurrency(plan.deductible),
    "Out of Pocket Maximum": plan.limit == 999999 ? "Unlimited" : notCoveredOrFormatCurrency(plan.limit),
    "Preventative Care Visits": "$0 Copay",
    "Preventative Care Prescriptions": "$0 Copay",
    "Primary Care Visits": notCoveredOrFormatCurrency(plan.estimated_costs.pcp_visit),

    "Specialist Visits": notCoveredOrFormatCurrency(plan.estimated_costs.specialist_visit),

    "Urgent Care": notCoveredOrFormatCurrency(plan.estimated_costs.urgent_care),
    "Other Prescription Coverage": drugTier1 == "Not Covered" ? drugTier1 : `${drugTier1} - ${drugTier4}`,
    "ER and Hospital": notCoveredOrFormatCurrency(plan.estimated_costs.emergency_room),
  };

  return (
    <Surface px={3} py={4} mt={4}>
      <BodyText pb={4} pl={2} size="large" weight="bold">
        Premium Overview
      </BodyText>
      <Table>
        <tbody>
          {Object.entries(planDetails).map(([name, value]) => (
            <TR>
              <TD>{name}</TD>
              <TD>{value}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </Surface>
  );
};

const EmployeeMECPlan = ({ contributions, prices, plan, backLink, onClickBack }) => {
  const familyInfo = useSelector(getFamilyInfo);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "open_enroll_or_decline_modal",
    });

    setIsModalOpen(true);
  };

  const closeModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "close_enroll_or_decline_modal",
    });

    setIsModalOpen(false);
  };
  return (
    <>
      <ChooseCoverageModal
        plan={plan}
        contributions={contributions}
        familyInfo={familyInfo}
        prices={[prices]}
        openModal={isModalOpen}
        closeModal={closeModal}
      />

      <CardSection>
        <Header level={7}>{capitalize(plan?.type)} coverage offered</Header>
      </CardSection>
      <MECHeader plan={plan} openModal={openModal} />
      <CardSection>
        <PremiumOverview prices={prices} familyInfo={familyInfo} contributions={contributions} />

        <PlanOverview plan={plan} />
      </CardSection>
      <CardSection>
        <RoutedBackButton href={backLink} onClick={onClickBack} />
        <EnrollOrDeclineButton openModal={openModal} />
      </CardSection>
    </>
  );
};

export default EmployeeMECPlan;
